import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle, XCircle, RefreshCw, AlertTriangle, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { detectEnvironment, getPostVerificationRedirect } from "@/utils/environmentRedirects";
import { useAuthVerificationContext } from "@/contexts/AuthVerificationContext";
import { authNavigationController, AuthFlowStage } from "@/controllers/AuthNavigationController";
import { 
  checkUserVerificationStatus, 
  resendVerificationEmail 
} from "@/services/VerificationStatusService";

type VerificationStatus = 'loading' | 'success' | 'error' | 'expired';

interface VerificationError {
  message: string;
  code?: string;
  actionable?: boolean;
}

const Verify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Make context usage optional to prevent provider errors
  const recordVerificationStep = async (step: string, status: "info" | "success" | "error" | "warning", details?: any) => {
    try {
      const { recordVerificationStep: contextRecord } = useAuthVerificationContext();
      return await contextRecord(step, status, details);
    } catch (error) {
      console.log(`[Verify] Context not available, skipping verification step: ${step}`);
      return false;
    }
  };
  
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [error, setError] = useState<VerificationError | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);
  const [detailedMessage, setDetailedMessage] = useState<string>('');
  const [canResendEmail, setCanResendEmail] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationStartTime] = useState(Date.now());

  // Extract verification parameters from URL
  const extractVerificationParams = () => {
    // Check URL parameters first
    let token = searchParams.get('token') || searchParams.get('code');
    let type = searchParams.get('type');
    
    // Check hash fragment if no token found in URL params
    if (!token && window.location.hash) {
      try {
        if (window.location.hash.includes('access_token=')) {
          token = window.location.hash.split('access_token=')[1]?.split('&')[0];
          type = type || 'recovery';
        } else {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          token = token || hashParams.get('token') || hashParams.get('code');
          type = type || hashParams.get('type');
        }
      } catch (error) {
        console.error('[Verify] Error parsing hash fragment:', error);
      }
    }
    
    return { token, type };
  };

  // Perform the actual verification
  const performVerification = async (token: string, type: string | null) => {
    try {
      setStatus('loading');
      setError(null);
      
      // Log verification attempt
      await recordVerificationStep("verify_page_processing", "info", {
        type,
        hasToken: !!token,
        environment: detectEnvironment().type,
        url: window.location.href
      });

      let verificationResult;
      
      if (type === 'recovery' || type === 'recover') {
        // Handle password recovery verification
        verificationResult = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery'
        });
        
        // Start recovery flow in controller
        authNavigationController.startFlow('recovery');
        authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
        
        if (verificationResult.data.user) {
          setUserEmail(verificationResult.data.user.email || '');
          setStatus('success');
          
          
          await recordVerificationStep("recovery_verification_success", "success", {
            userId: verificationResult.data.user.id,
            email: verificationResult.data.user.email
          });
          
          return;
        }
      } else {
        // Handle signup/invite verification
        verificationResult = await supabase.auth.verifyOtp({
          token_hash: token,
          type: type === 'invite' ? 'invite' : 'signup'
        });
        
        // Start appropriate flow in controller
        if (type === 'signup') {
          authNavigationController.startFlow('signup');
        } else if (type === 'invite') {
          authNavigationController.startFlow('invite');
        }
        authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
      }
      
      if (verificationResult.error) {
        throw verificationResult.error;
      }
      
      const user = verificationResult.data.user;
      if (!user) {
        throw new Error('No user data received after verification');
      }
      
      setUserEmail(user.email || '');
      setStatus('success');
      
      // Determine redirect based on user context
      const { data: hasVendorRole } = await supabase.rpc('has_role', {
        '_role': 'vendor'
      });
      
      const isAdmin = user.user_metadata?.role === 'admin';
      
      // Check if profile setup is needed
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();
      
      const hasCompletedProfile = profile && profile.display_name;
      
      const redirectUrl = getPostVerificationRedirect({
        isVendor: hasVendorRole,
        isAdmin,
        hasCompletedProfile: !!hasCompletedProfile
      });
      
      await recordVerificationStep("verification_success", "success", {
        userId: user.id,
        email: user.email,
        hasVendorRole,
        isAdmin,
        hasCompletedProfile
      });
      
      
    } catch (error: any) {
      console.error('[Verify] Verification failed:', error);
      
      let errorInfo: VerificationError = {
        message: 'Verification failed',
        actionable: true
      };
      
      if (error.message?.includes('expired')) {
        // Check if user is already authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // User is already signed in, this means verification likely already completed
          setUserEmail(session.user.email || '');
          setStatus('success'); // Show success state since they're verified
          setDetailedMessage("Your email was already verified. You can continue using the app!");
          
          await recordVerificationStep("verification_already_completed", "success", {
            userId: session.user.id,
            email: session.user.email,
            note: "User clicked expired link but was already authenticated"
          });
          
          return; // Exit early to prevent error toast
        } else {
          // Enhanced expired token handling
          const userStatus = await checkUserVerificationStatus(userEmail);
          if (userStatus.isVerified) {
            setStatus('success');
            setUserEmail(userStatus.email || '');
            setDetailedMessage("Your email is already verified! The link expired but you can sign in normally.");
            return;
          }
          
          errorInfo = {
            message: 'This verification link has expired (typically after 24 hours).',
            code: 'expired',
            actionable: true
          };
          setStatus('expired');
          setDetailedMessage("Don't worry - this is normal for security. We can send you a fresh verification link.");
          setCanResendEmail(true);
        }
      } else if (error.message?.includes('invalid')) {
        // Check if user is actually verified despite invalid token
        const userStatus = await checkUserVerificationStatus(userEmail);
        if (userStatus.isVerified) {
          setStatus('success');
          setUserEmail(userStatus.email || '');
          setDetailedMessage("This link was already used, but your email is verified. You can sign in normally.");
          return;
        }
        
        errorInfo = {
          message: 'This verification link is invalid or has already been used.',
          code: 'invalid',
          actionable: true
        };
        setStatus('error');
        setDetailedMessage("If you're still having trouble signing in, we can send you a new verification email.");
        setCanResendEmail(true);
      } else {
        errorInfo = {
          message: error.message || 'An unexpected error occurred during verification',
          code: error.code,
          actionable: true
        };
        setStatus('error');
      }
      
      setError(errorInfo);
      
      await recordVerificationStep("verification_error", "error", {
        message: errorInfo.message,
        code: errorInfo.code,
        retryCount
      });
      
      toast({
        variant: "destructive",
        title: "Verification failed",
        description: errorInfo.message,
      });
    }
  };

  // Handle resending verification email
  const handleResendEmail = async () => {
    if (!userEmail) {
      toast({
        variant: "destructive",
        title: "Cannot resend email",
        description: "Email address not found. Please try signing up again."
      });
      return;
    }

    setIsResending(true);
    try {
      const result = await resendVerificationEmail(userEmail);
      
      if (result.success) {
        toast({
          title: "Email sent!",
          description: result.message
        });
        setCanResendEmail(false);
        // Re-enable resend after a delay
        setTimeout(() => setCanResendEmail(true), 60000); // 1 minute
        
        await recordVerificationStep("resend_email_success", "success", {
          email: userEmail
        });
      } else {
        toast({
          variant: "destructive",
          title: "Failed to resend email",
          description: result.message
        });
        
        await recordVerificationStep("resend_email_failed", "error", {
          email: userEmail,
          error: result.message
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to resend verification email. Please try again."
      });
    } finally {
      setIsResending(false);
    }
  };

  // Handle retry
  const handleRetry = () => {
    const { token, type } = extractVerificationParams();
    if (token) {
      setRetryCount(prev => prev + 1);
      performVerification(token, type);
    }
  };

  // Handle redirect to auth page
  const handleGoToAuth = () => {
    // Check if this was a password recovery flow
    const { type } = extractVerificationParams();
    if (type === 'recovery' || type === 'recover') {
      navigate('/auth?view=resetPassword');
    } else {
      navigate('/auth');
    }
  };

  // Main verification effect
  useEffect(() => {
    const { token, type } = extractVerificationParams();
    
    console.log('[Verify] Processing verification request:', {
      hasToken: !!token,
      type,
      environment: detectEnvironment().type,
      url: window.location.href
    });
    
    if (!token) {
      // No token found - check if user is already authenticated (Supabase already processed verification)
      const checkAuthStatus = async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.user) {
            // User is already authenticated - verification was successful
            setUserEmail(session.user.email || '');
            setStatus('success');
            
            await recordVerificationStep("verification_already_completed", "success", {
              userId: session.user.id,
              email: session.user.email,
              type,
              note: "User reached verify page but was already authenticated"
            });
            
            // Determine redirect based on user context
            const { data: hasVendorRole } = await supabase.rpc('has_role', {
              '_role': 'vendor'
            });
            
            const isAdmin = session.user.user_metadata?.role === 'admin';
            
            // Check if profile setup is needed
            const { data: profile } = await supabase
              .from('profiles')
              .select('display_name')
              .eq('id', session.user.id)
              .single();
            
            const hasCompletedProfile = profile && profile.display_name;
            
            const redirectUrl = getPostVerificationRedirect({
              isVendor: hasVendorRole,
              isAdmin,
              hasCompletedProfile: !!hasCompletedProfile
            });
            
            
          } else {
            // Not authenticated and no token - show error
            setStatus('error');
            setError({
              message: 'No verification token found and you are not signed in. Please try signing in with your email and password.',
              actionable: true
            });
          }
        } catch (error) {
          console.error('[Verify] Error checking auth status:', error);
          setStatus('error');
          setError({
            message: 'Unable to verify your authentication status. Please try signing in.',
            actionable: true
          });
        }
      };
      
      // Small delay to show loading state
      setTimeout(checkAuthStatus, 500);
      return;
    }
    
    // Token found - proceed with normal verification flow
    const timer = setTimeout(() => {
      performVerification(token, type);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchParams]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
      case 'expired':
        return <XCircle className="h-12 w-12 text-red-500" />;
      default:
        return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'loading':
        return {
          title: "Verifying your email...",
          description: "Please wait while we verify your email address."
        };
      case 'success':
        return {
          title: "Email verified successfully!",
          description: detailedMessage || (userEmail 
            ? `Welcome! Your email ${userEmail} has been verified. You can now sign in with your account.`
            : `Your email has been verified. You can now sign in with your account.`)
        };
      case 'error':
        return {
          title: "Verification failed",
          description: detailedMessage || error?.message || "We couldn't verify your email address."
        };
      case 'expired':
        return {
          title: "Link expired",
          description: detailedMessage || error?.message || "This verification link has expired."
        };
      default:
        return {
          title: "Verifying...",
          description: "Processing your verification request."
        };
    }
  };

  const { title, description } = getStatusMessage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          {getStatusIcon()}
          <div>
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription className="text-base mt-2">
              {description}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Show helpful alerts */}
          {status === 'expired' && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Verification links expire after 24 hours for security. This is normal and we can send you a fresh one.
              </AlertDescription>
            </Alert>
          )}
          
          {status === 'success' && (
            <div className="flex items-center justify-center">
              <Badge variant="outline" className="text-green-700 border-green-300">
                Verified in {Math.round((Date.now() - verificationStartTime) / 1000)}s
              </Badge>
            </div>
          )}

          {status === 'error' || status === 'expired' ? (
            <div className="space-y-3">
              {canResendEmail && userEmail && (
                <Button 
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="w-full"
                  size="lg"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send New Verification Email"
                  )}
                </Button>
              )}
              
              {retryCount < 2 && (
                <Button 
                  onClick={handleRetry}
                  className="w-full"
                  variant="outline"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try again
                </Button>
              )}
              
              <Button 
                onClick={handleGoToAuth}
                className="w-full"
                variant={canResendEmail ? "outline" : "default"}
              >
                Go to sign in
              </Button>
            </div>
          ) : status === 'success' ? (
            <Button 
              onClick={handleGoToAuth}
              className="w-full"
              size="lg"
            >
              {(() => {
                const { type } = extractVerificationParams();
                return (type === 'recovery' || type === 'recover') ? 'Reset Password' : 'Continue to Sign In';
              })()}
            </Button>
          ) : null}
          
          {/* Debug info in development */}
          {process.env.NODE_ENV === 'development' && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Debug Info</summary>
              <div className="mt-2 p-2 bg-muted rounded text-xs font-mono">
                <div>Status: {status}</div>
                <div>Retry Count: {retryCount}</div>
                <div>Can Resend: {canResendEmail.toString()}</div>
                <div>Email: {userEmail}</div>
                <div>URL: {window.location.href}</div>
              </div>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Verify;